import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Button,
  Card,
  CardBody,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  VStack,
  Text,
  Alert,
  AlertIcon,
} from '@chakra-ui/react'
import { Eye, EyeOff, Lock, Mail } from 'lucide-react'
import { loginSchema, type LoginFormData } from '@/lib/validations/auth'
import { useAuthStore } from '@/store/auth'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useSiteSettings } from '@/hooks/useSiteSettings'

export function LoginForm() {
  const navigate = useNavigate()
  const [showPassword, setShowPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(true)
  const [error, setError] = useState('')

  const { login, isLoading } = useAuthStore()
  const { settings: siteSettings } = useSiteSettings()
  const colors = useThemeColors()

  const siteName = import.meta.env.VITE_APP_NAME || 'Sign In'
  const siteDescription =
    import.meta.env.VITE_APP_DESCRIPTION || 'Enter your credentials to access your account'

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  })

  // Prevent body scroll on login page
  useEffect(() => {
    const html = document.documentElement
    const body = document.body
    html.style.overflow = 'hidden'
    html.style.height = '100%'
    body.style.overflow = 'hidden'
    body.style.height = '100%'
    body.style.margin = '0'
    body.style.padding = '0'
    return () => {
      html.style.overflow = ''
      html.style.height = ''
      body.style.overflow = ''
      body.style.height = ''
      body.style.margin = ''
      body.style.padding = ''
    }
  }, [])

  const onSubmit = async (data: LoginFormData) => {
    try {
      setError('')

      const result = await login({
        email: data.email,
        password: data.password,
        remember_me: rememberMe,
        application: 'admin-support',
      })

      if (result.twoFactorSetupRequired) {
        navigate(
          `/two-factor/setup?email=${encodeURIComponent(data.email)}&remember_me=${rememberMe ? '1' : '0'}`,
          { replace: true }
        )
        return
      }

      if (result.twoFactorRequired) {
        navigate(
          `/two-factor?email=${encodeURIComponent(data.email)}&remember_me=${rememberMe ? '1' : '0'}`,
          { replace: true }
        )
        return
      }

      if (result.success) {
        navigate('/dashboard', { replace: true })
      } else {
        setError(result.message || 'Login failed')
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || err.message || 'Login failed. Please check your credentials.'
      setError(errorMessage)

      if (import.meta.env.DEV) {
        console.error('[LoginForm] Login error:', {
          message: err.message,
          response: err.response?.data,
          status: err.response?.status,
        })
      }
    }
  }

  return (
    <Box minH="100vh" bg={colors.bg} display="flex" alignItems="center" justifyContent="center">
      <Card maxW="md" w="full" bg={colors.cardBg}>
        <CardBody p={6}>
          <VStack spacing={4}>
            <Box textAlign="center">
              <Box mb={2} display="flex" justifyContent="center">
                <img
                  src={siteSettings.auth_logo || '/assets/img/als-logo-2.png'}
                  alt={siteName}
                  style={{ maxWidth: '300px', height: 'auto' }}
                />
              </Box>

              <Heading size="lg" color={colors.heading} mb={1}>
                {siteName}
              </Heading>

              <Text color={colors.text} fontSize="sm">
                {siteDescription}
              </Text>
            </Box>

            {error && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                {error}
              </Alert>
            )}

            <Box as="form" onSubmit={handleSubmit(onSubmit)} w="100%">
              <VStack spacing={3} w="100%">
                <FormControl isInvalid={!!errors.email} isDisabled={isLoading}>
                  <FormLabel>Email</FormLabel>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <Mail size={20} />
                    </InputLeftElement>
                    <Input
                      {...register('email')}
                      type="email"
                      placeholder="Enter your email"
                      pl="10"
                      isDisabled={isLoading}
                    />
                  </InputGroup>
                  <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.password} isDisabled={isLoading}>
                  <FormLabel>Password</FormLabel>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <Lock size={20} />
                    </InputLeftElement>
                    <Input
                      {...register('password')}
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      pl="10"
                      isDisabled={isLoading}
                    />
                    <InputRightElement width="3rem">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowPassword((s) => !s)}
                        type="button"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        h="1.75rem"
                        minW="1.75rem"
                        p={0}
                        isDisabled={isLoading}
                      >
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </Button>
                    </InputRightElement>
                  </InputGroup>
                  <FormErrorMessage>{errors.password?.message}</FormErrorMessage>
                </FormControl>

                <Box
                  mt={1}
                  w="100%"
                  display="grid"
                  gridTemplateColumns="1fr 1fr"
                  gap={3}
                  alignItems="center"
                >
                  <Box textAlign="left">
                    <Checkbox
                      isChecked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      colorScheme="blue"
                      size="sm"
                      isDisabled={isLoading}
                    >
                      <Text fontSize="sm" color={colors.link}>
                        Remember me
                      </Text>
                    </Checkbox>
                  </Box>
                  <Box textAlign="right">
                    <Button
                      variant="link"
                      color="blue.500"
                      fontSize="sm"
                      onClick={() => navigate('/forgot-password')}
                      _hover={{ textDecoration: 'underline' }}
                      isDisabled={isLoading}
                    >
                      Forgot your password?
                    </Button>
                  </Box>
                </Box>

                <Button
                  type="submit"
                  bg="blue.500"
                  color="white"
                  _hover={{ bg: 'blue.600' }}
                  size="lg"
                  w="full"
                  isLoading={isLoading}
                  loadingText="Signing in..."
                  isDisabled={isLoading}
                >
                  Sign In
                </Button>
              </VStack>
            </Box>
          </VStack>
        </CardBody>
      </Card>
    </Box>
  )
}
